/**
 * Live `AiProvider` over the connection contract, `/t/{slug}/api/v1/ai`.
 *
 * Transport is injected: the browser uses `fetchTransport.ts` (same-origin,
 * session cookie, 401 handling shared with `apiClient`), tests use a mock.
 *
 * Rules:
 *   - every 2xx body is validated (`parse.ts`); an invalid body is an
 *     `AiInvalidResponseError`, never a partially shown value;
 *   - every non-2xx body becomes an `AiContractError` built defensively;
 *   - NO automatic retry of any request, in particular never of
 *     `POST /runs` with a new key. A caller that wants to learn the outcome of
 *     an unknown run reads it with `GET /runs/idempotency/:key` (read-only; no
 *     provider call, no reserve) and then `GET /runs/:id` / `GET /turns/:id`;
 *   - a timeout or abort only stops the browser's wait. It says nothing about
 *     the server run or its hold: `outcomeUnknown` is true for writes;
 *   - there is no fallback data: a failure is thrown, never replaced.
 */
import type {
  AiActivation,
  AiBalance,
  AiBudget,
  AiBudgetUpdate,
  AiQuote,
  AiQuoteRequest,
  AiRunRecovery,
  AiRunRequest,
  AiRunStatus,
  AiSettings,
  AiSettingsUpdate,
  AiTurn,
  AiUsageResponse,
  AiUsageScope,
  AiWalletPackagesResponse,
  AiTopupCheckoutRequest,
  AiTopupCheckoutResponse,
  AiTopupVerifyResponse,
  AiTopupOrder,
} from './contract.ts';
import {
  AiAbortedError,
  AiContractError,
  AiInvalidResponseError,
  AiNetworkError,
  AiTimeoutError,
} from './errors.ts';
import {
  parseActivation,
  parseWelcomeCredit,
  parseBalance,
  parseBudgets,
  parseQuote,
  parseRunRecovery,
  parseRunStatus,
  parseSaved,
  parseSettings,
  parseTurn,
  parseUsage,
  parseWalletPackages,
  parseTopupCheckout,
  parseTopupVerify,
  parseTopupOrders,
} from './parse.ts';
import type { AiCallOptions, AiProvider } from './provider.ts';

export interface AiTransportRequest {
  readonly method: 'GET' | 'POST' | 'PUT';
  /** Absolute path including the tenant prefix. */
  readonly path: string;
  readonly body?: unknown;
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal: AbortSignal;
}

export interface AiTransportResponse {
  readonly status: number;
  /** Parsed JSON, or `undefined` when the body was empty / not JSON. */
  readonly body: unknown;
}

/** Resolves for ANY HTTP status; rejects only when no response arrived. */
export type AiTransport = (request: AiTransportRequest) => Promise<AiTransportResponse>;

export interface HttpProviderOptions {
  /** Tenant slug; the base path is `/t/{slug}/api/v1/ai`. */
  readonly slug: string;
  readonly transport: AiTransport;
  /** Per-call deadline. Default 30 s. */
  readonly timeoutMs?: number;
  /** Key for writes that have none of their own (quote, settings). Default `crypto.randomUUID`. */
  readonly newKey?: () => string;
}

const DEFAULT_TIMEOUT_MS = 30_000;

export function aiBasePath(slug: string): string {
  return `/t/${encodeURIComponent(slug)}/api/v1/ai`;
}

export function createHttpProvider(options: HttpProviderOptions): AiProvider {
  const base = aiBasePath(options.slug);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const newKey = options.newKey ?? (() => crypto.randomUUID());
  const inflight = new Set<AbortController>();
  let disposed = false;

  async function call<T>(
    method: AiTransportRequest['method'],
    path: string,
    parse: (body: unknown) => T,
    extra: {
      readonly body?: unknown;
      readonly headers?: Record<string, string>;
      readonly options?: AiCallOptions | undefined;
    } = {},
  ): Promise<T> {
    const isWrite = method !== 'GET';
    if (disposed) throw new AiAbortedError(false);
    const caller = extra.options?.signal;
    if (caller?.aborted) throw new AiAbortedError(false);

    const controller = new AbortController();
    inflight.add(controller);
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onCallerAbort = () => controller.abort();
    caller?.addEventListener('abort', onCallerAbort, { once: true });

    const stopped = new Promise<never>((_, reject) => {
      controller.signal.addEventListener(
        'abort',
        () => reject(timedOut ? new AiTimeoutError(isWrite) : new AiAbortedError(isWrite)),
        { once: true },
      );
    });
    stopped.catch(() => undefined);

    try {
      const pending = options.transport({
        method,
        path: `${base}${path}`,
        ...(extra.body !== undefined ? { body: extra.body } : {}),
        ...(extra.headers ? { headers: extra.headers } : {}),
        signal: controller.signal,
      });
      pending.catch(() => undefined);
      let response: AiTransportResponse;
      try {
        response = await Promise.race([pending, stopped]);
      } catch (error) {
        if (error instanceof AiTimeoutError || error instanceof AiAbortedError) throw error;
        throw new AiNetworkError(isWrite);
      }
      if (response.status < 200 || response.status >= 300) {
        throw AiContractError.fromBody(response.status, response.body);
      }
      return parse(response.body);
    } finally {
      clearTimeout(timer);
      caller?.removeEventListener('abort', onCallerAbort);
      inflight.delete(controller);
    }
  }

  const write = (key: string) => ({ 'idempotency-key': key });

  return {
    kind: 'live',

    getActivation: (o) => call('GET', '/activation', parseActivation, { options: o }),

    async setOptIn(optedIn, o) {
      // The backend answers `{ saved: true }`; the new state is read back, never assumed.
      await call('PUT', '/activation', parseSaved, {
        body: { optedIn },
        headers: write(newKey()),
        options: o,
      });
      return call<AiActivation>('GET', '/activation', parseActivation, { options: o });
    },

    claimWelcomeCredit: (o) =>
      call('POST', '/wallet/welcome-credit', parseWelcomeCredit, {
        body: {},
        headers: write(newKey()),
        options: o,
      }),

    getBalance: (o) => call<AiBalance>('GET', '/balance', parseBalance, { options: o }),

    getBudget: (o) => call<readonly AiBudget[]>('GET', '/budgets', parseBudgets, { options: o }),

    async updateBudget(update: AiBudgetUpdate, o) {
      await call('PUT', '/budgets', parseSaved, {
        body: update,
        headers: write(newKey()),
        options: o,
      });
      return call<readonly AiBudget[]>('GET', '/budgets', parseBudgets, { options: o });
    },

    getSettings: (o) => call<AiSettings>('GET', '/settings', parseSettings, { options: o }),

    async updateSettings(update: AiSettingsUpdate, o) {
      await call('PUT', '/settings', parseSaved, {
        body: update,
        headers: write(newKey()),
        options: o,
      });
      return call<AiSettings>('GET', '/settings', parseSettings, { options: o });
    },

    createQuote: (request: AiQuoteRequest, o) =>
      call<AiQuote>('POST', '/quotes', parseQuote, {
        body: {
          question: request.question,
          ...(request.tool !== undefined ? { tool: request.tool } : {}),
          ...(request.domain !== undefined ? { domain: request.domain } : {}),
          ...(request.period !== undefined ? { period: request.period } : {}),
          ...(request.deepSearch !== undefined ? { deepSearch: request.deepSearch } : {}),
          ...(request.conversationId !== undefined
            ? { conversationId: request.conversationId }
            : {}),
        },
        headers: write(newKey()),
        options: o,
      }),

    createRun: (request: AiRunRequest, o) =>
      call<AiTurn>('POST', '/runs', parseTurn, {
        body: { quoteId: request.quoteId, idempotencyKey: request.idempotencyKey },
        // The run's own key is the transport key too: never a fresh one.
        headers: write(request.idempotencyKey),
        options: o,
      }),

    getRun: (runId, o) =>
      call<AiRunStatus>('GET', `/runs/${encodeURIComponent(runId)}`, parseRunStatus, {
        options: o,
      }),

    getRunByIdempotencyKey: (key, o) =>
      call<AiRunRecovery>('GET', `/runs/idempotency/${encodeURIComponent(key)}`, parseRunRecovery, {
        options: o,
      }),

    getTurn: (turnId, o) =>
      call<AiTurn>('GET', `/turns/${encodeURIComponent(turnId)}`, parseTurn, { options: o }),

    getUsage: (scope: AiUsageScope, o) =>
      call<AiUsageResponse>('GET', `/usage?scope=${scope}`, (body) => parseUsage(body, scope), {
        options: o,
      }),

    getWalletPackages: (o) =>
      call<AiWalletPackagesResponse>('GET', '/wallet/packages', parseWalletPackages, {
        options: o,
      }),

    createTopupCheckout: (request: AiTopupCheckoutRequest, o) =>
      call<AiTopupCheckoutResponse>('POST', '/wallet/topup/checkout', parseTopupCheckout, {
        body: {
          ...(request.amountToman === undefined ? {} : { amountToman: request.amountToman }),
          ...(request.packageId === undefined ? {} : { packageId: request.packageId }),
          ...(request.callbackUrl === undefined ? {} : { callbackUrl: request.callbackUrl }),
        },
        headers: write(newKey()),
        options: o,
      }),

    verifyTopup: (authority: string, status?: string, o?: AiCallOptions) =>
      call<AiTopupVerifyResponse>('POST', '/wallet/topup/verify', parseTopupVerify, {
        body: { authority, ...(status === undefined ? {} : { status }) },
        headers: write(newKey()),
        options: o,
      }),

    reconcileTopup: (orderId, o) =>
      call<AiTopupVerifyResponse>(
        'POST',
        `/wallet/topup/orders/${encodeURIComponent(orderId)}/reconcile`,
        parseTopupVerify,
        { body: {}, headers: write(newKey()), options: o },
      ),

    getTopupOrders: (limit?: number, o?: AiCallOptions) =>
      call<readonly AiTopupOrder[]>(
        'GET',
        `/wallet/topup/orders${limit ? `?limit=${limit}` : ''}`,
        parseTopupOrders,
        { options: o },
      ),

    dispose() {
      disposed = true;
      for (const controller of inflight) controller.abort();
      inflight.clear();
    },
  };
}

export { AiInvalidResponseError };
